import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ACTION_TYPES, useSubmissionSearchDispatch, useSubmissionSearchState } from './submission-search-context';
import SubmissionAppService from '../../../../application/platform/ser/submission/submission-app-service';
import { useActionRunner } from '../../../../hooks/use-action-runner';
import { useSearchActions } from '../../../../hooks/use-search-actions';
import { ISubmissionPagedRequest } from '../../../../model/ser/submission/request/submission-paged-request';
import { parseIdFilter } from '../../../../utils/filter-options';
import { EMPTY_FILTERS, parseDeepLink, validatePeriods } from '../submission-search-utils';

// sessionClientId: cliente do usuário de cliente (os usuários da empresa filtram pela entidade)
export const useSubmissionSearchController = (sessionClientId: number | undefined, isClientUser: boolean) => {
    const dispatch = useSubmissionSearchDispatch();
    const state = useSubmissionSearchState();
    const [searchParams, setSearchParams] = useSearchParams();
    const { run, runSaving, isSaving, showSuccess, showError } = useActionRunner();
    const { updateParameters, triggerSearch, handleSearch: search, handleClearFilters, handlePaginationChange } = useSearchActions<ISubmissionPagedRequest>(
        dispatch, ACTION_TYPES, EMPTY_FILTERS,
    );
    const vehiclesClientId = isClientUser ? sessionClientId : state.parameters.clientId;

    useEffect(() => {
        dispatch({ type: ACTION_TYPES.applyDeepLink, payload: parseDeepLink(searchParams) });
        if (Array.from(searchParams.keys()).length) setSearchParams({}, { replace: true });
        run('Não foi possível carregar os status das remessas', async () => {
            dispatch({ type: ACTION_TYPES.setStatuses, payload: await SubmissionAppService.listStatus() });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A lista de veículos depende da entidade (usuários da empresa só veem veículos depois de escolher uma)
    useEffect(() => {
        if (!vehiclesClientId) {
            dispatch({ type: ACTION_TYPES.setVehicles, payload: [] });
            return;
        }
        run('Não foi possível carregar os veículos de publicação', async () => {
            dispatch({ type: ACTION_TYPES.setVehicles, payload: await SubmissionAppService.listVehicles(vehiclesClientId) });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehiclesClientId]);

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'input-title') updateParameters({ title: value || undefined });
        if (name === 'input-content') updateParameters({ content: value || undefined });
        if (name === 'input-batch') updateParameters({ batchId: parseIdFilter(value) });
        if (name === 'select-vehicle') updateParameters({ vehicleId: parseIdFilter(value) });
        if (name === 'select-status') updateParameters({ statusIds: (value as (string | number)[]).map(Number) });
        if (name === 'select-type') updateParameters({ types: (value as (string | number)[]).map(Number) });
        if (name === 'select-client') {
            const clientId = parseIdFilter(value);
            if (clientId !== state.parameters.clientId) updateParameters({ clientId, vehicleId: undefined });
        }
    }

    function handleDateChange(field: 'startDate' | 'endDate' | 'publishStartDate' | 'publishEndDate', value: string | null) {
        updateParameters({ [field]: value ?? undefined });
    }

    function handleSearch() {
        const errors = validatePeriods(state.parameters);
        if (Object.keys(errors).length) {
            dispatch({ type: ACTION_TYPES.setPeriodErrors, payload: errors });
            return;
        }
        search();
    }

    function openDetail(id: number) {
        dispatch({ type: ACTION_TYPES.openDetail, payload: id });
    }

    function closeDetail() {
        dispatch({ type: ACTION_TYPES.closeDetail });
    }

    function openBatch(batchId: number) {
        dispatch({ type: ACTION_TYPES.openBatch, payload: batchId });
    }

    function closeBatch() {
        dispatch({ type: ACTION_TYPES.closeBatch });
    }

    function filterByBatch(batchId: number) {
        updateParameters({ ...EMPTY_FILTERS, batchId, page: 0 });
        dispatch({ type: ACTION_TYPES.closeBatch });
        triggerSearch();
    }

    function openCancel(target: { id: number; title?: string }) {
        dispatch({ type: ACTION_TYPES.openCancel, payload: target });
    }

    function closeCancel() {
        dispatch({ type: ACTION_TYPES.closeCancel });
    }

    function refresh() {
        dispatch({ type: ACTION_TYPES.refresh });
    }

    function confirmCancel(reason: string) {
        const target = state.cancelTarget;
        if (!target) return;
        return runSaving('Não foi possível cancelar a remessa', async () => {
            try {
                await SubmissionAppService.cancel(target.id, reason);
                showSuccess('Remessa cancelada');
            } catch (error: any) {
                // O status pode ter mudado (ex.: recebida pela diagramação): mostra a mensagem e recarrega
                showError('Não foi possível cancelar a remessa', error);
            } finally {
                closeCancel();
                refresh();
            }
        });
    }

    return {
        isCancelling: isSaving,
        handleOnChange, handleDateChange, handleSearch, handleClearFilters, handlePaginationChange,
        openDetail, closeDetail, openBatch, closeBatch, filterByBatch,
        openCancel, closeCancel, confirmCancel, refresh,
    };
};
